import React, { useState, useMemo } from 'react';
import { Modal, Table, Icon, Label, Accordion, Input, Dropdown } from 'semantic-ui-react';
import moment from 'moment';
import { formatCurrency } from '../services';

const SURFACE_LABELS = {
    cdpReport: 'CDP Report',
    sageExport: 'Sage Export',
    earningsReport: 'Sent'
};

const CONTRACT_LABEL_CONFIG = {
    'Prod Hurdle':          { className: 'primaryColor additionals',                        label: 'Prod Hurdle' },
    'Collections':          { className: 'bg-color-collections color-white additionals',    label: 'Collections' },
    'Greater Of':           { className: 'bg-color-primary color-white additionals',        label: 'Greater Of' },
    'GOZ':                  { style: { color: 'black', backgroundColor: '#FF00FF' },        label: 'GOZ' },
    'GOX':                  { style: { color: 'white', backgroundColor: '#008820ff' },      label: 'GOX' },
    'Sum Of':               { className: 'primaryColor additionals',                        label: 'Sum Of' },
    'UKG HYN':              { className: 'additionals',                                     label: 'UKG HYN' },
    'TMPHYN':               { className: 'bg-color-collections color-white additionals',    label: 'TMPHYN' },
    'TMPDOC':               { className: 'bg-color-collections color-white additionals',    label: 'TMPDOC' },
    'Greater Of w/HYN':     { style: { backgroundColor: '#07036B', color: '#ffffff' },      label: 'GO/HYN' },
    'Sum Of w/HYN':         { className: 'primaryColor additionals',                        label: 'SO/HYN' },
    'Collections w/HYN':    { className: 'bg-color-collections additionals',                label: 'CO/HYN' },
    'GOX w/HYN':            { style: { color: 'black', backgroundColor: '#5FE47F' },        label: 'GOX/HYN' },
    'Collections Offset':   { className: 'bg-color-collections additionals',                label: 'CO/Offset' },
};

const LOG_STATUS_ICON = {
    pending:  { name: 'circle notch', color: 'grey',  loading: true },
    ok:       { name: 'check circle', color: 'green' },
    not_sent: { name: 'minus circle', color: 'grey' },
    error:    { name: 'warning sign', color: 'red' },
};

const round = (v) => Math.round((Number(v) || 0) * 100) / 100;

const maxDelta = (values) => {
    const nums = Object.values(values || {}).filter(v => v !== null && v !== undefined).map(Number);
    if (nums.length < 2) return 0;
    return round(Math.max(...nums) - Math.min(...nums));
};

const ContractLabel = ({ contractType }) => {
    const config = CONTRACT_LABEL_CONFIG[contractType];
    if (!config) return <span>{contractType || '—'}</span>;
    return <Label className={config.className || 'additionals'} style={config.style} content={config.label} />;
};

const renderValueCell = (value) => (value === null || value === undefined ? '—' : formatCurrency(value));

const TotalPayConsistencyModal = ({ open, loading, results = [], progress = { completed: 0, total: 0 }, log = [], onClose }) => {
    const [logOpen, setLogOpen]         = useState(false);
    const [deltaMin, setDeltaMin]       = useState('');
    const [filterContract, setContract] = useState('');
    const [filterProvider, setProvider] = useState('');
    const [filterStatus, setStatus]     = useState('');

    const mismatchCount = results.filter(r => !r.match).length;
    const { completed, total } = progress;

    const contractOptions = useMemo(() => {
        const seen = new Set();
        const opts = [{ key: '', value: '', text: 'All contracts' }];
        results.forEach(r => {
            if (r.contractType && !seen.has(r.contractType)) {
                seen.add(r.contractType);
                opts.push({ key: r.contractType, value: r.contractType, text: CONTRACT_LABEL_CONFIG[r.contractType]?.label || r.contractType });
            }
        });
        return opts;
    }, [results]);

    const providerOptions = useMemo(() => {
        const opts = [{ key: '', value: '', text: 'All providers' }];
        results.forEach(r => {
            const name = r.doctorName || r.doctorId;
            if (name) opts.push({ key: r.doctorId, value: r.doctorId, text: name });
        });
        return opts;
    }, [results]);

    const STATUS_OPTIONS = [
        { key: '',         value: '',         text: 'All statuses' },
        { key: 'match',    value: 'match',    text: 'Match'        },
        { key: 'mismatch', value: 'mismatch', text: 'Mismatch'     },
        { key: 'error',    value: 'error',    text: 'Error'        },
    ];

    const visibleResults = useMemo(() => {
        const minDelta = parseFloat(deltaMin);
        return results.filter(r => {
            if (filterContract && r.contractType !== filterContract) return false;
            if (filterProvider && r.doctorId !== filterProvider) return false;
            if (!isNaN(minDelta) && deltaMin !== '') {
                if (maxDelta(r.values) < minDelta) return false;
            }
            if (filterStatus === 'match'    && !(r.match && !r.error))  return false;
            if (filterStatus === 'mismatch' && !(!r.match && !r.error)) return false;
            if (filterStatus === 'error'    && !r.error)                return false;
            return true;
        });
    }, [results, deltaMin, filterContract, filterProvider, filterStatus]);

    return (
        <Modal open={open} onClose={onClose} closeIcon size='large'>

            {/* ── Header: title + live count/status badge ── */}
            <Modal.Header>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span>Verify Total Pay</span>
                    {(loading || results.length > 0) && (
                        <Label color={loading ? 'grey' : mismatchCount ? 'red' : 'green'}>
                            {loading
                                ? `${completed} / ${total} — ${total > 0 ? Math.round((completed / total) * 100) : 0}%`
                                : mismatchCount
                                    ? `${mismatchCount} mismatch${mismatchCount === 1 ? '' : 'es'}`
                                    : 'All match'}
                        </Label>
                    )}
                </div>
            </Modal.Header>

            {/* ── Content: filters + table ── */}
            <Modal.Content scrolling style={{ minHeight: 320 }}>

                {/* Filter bar */}
                {results.length > 0 && (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                        <Input
                            icon='balance scale'
                            iconPosition='left'
                            placeholder='Min delta ($)'
                            type='number'
                            min='0'
                            step='0.01'
                            value={deltaMin}
                            onChange={(_, { value }) => setDeltaMin(value)}
                            style={{ width: 160 }}
                            size='small'
                        />
                        <Dropdown
                            selection
                            search
                            options={contractOptions}
                            value={filterContract}
                            onChange={(_, { value }) => setContract(value)}
                            placeholder='Contract'
                            style={{ minWidth: 160 }}
                            trigger={<><Icon name='file alternate outline' />{filterContract || 'Contract'}</>}
                            size='small'
                        />
                        <Dropdown
                            selection
                            search
                            options={providerOptions}
                            value={filterProvider}
                            onChange={(_, { value }) => setProvider(value)}
                            placeholder='Provider'
                            style={{ minWidth: 200 }}
                            trigger={<><Icon name='user outline' />{filterProvider ? (providerOptions.find(o => o.value === filterProvider)?.text || 'Provider') : 'Provider'}</>}
                            size='small'
                        />
                        <Dropdown
                            selection
                            options={STATUS_OPTIONS}
                            value={filterStatus}
                            onChange={(_, { value }) => setStatus(value)}
                            style={{ minWidth: 150 }}
                            trigger={<><Icon name='dot circle outline' />{(STATUS_OPTIONS.find(o => o.value === filterStatus) || STATUS_OPTIONS[0]).text}</>}
                            size='small'
                        />
                        {(deltaMin !== '' || filterContract || filterProvider || filterStatus) && (
                            <Label
                                as='a'
                                style={{ cursor: 'pointer' }}
                                onClick={() => { setDeltaMin(''); setContract(''); setProvider(''); setStatus(''); }}
                            >
                                <Icon name='times' /> Clear
                            </Label>
                        )}
                        {(deltaMin !== '' || filterContract || filterProvider || filterStatus) && (
                            <span style={{ color: '#888', fontSize: '0.9em' }}>
                                {visibleResults.length} of {results.length} shown
                            </span>
                        )}
                    </div>
                )}

                {/* Table or loading placeholder */}
                {loading && results.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa' }}>
                        <Icon name='circle notch' loading size='large' />
                        <div style={{ marginTop: 10 }}>Starting checks for {total} dentist{total !== 1 ? 's' : ''}…</div>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table-hover show-active-sort" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.92em' }}>
                            <thead>
                                <tr style={{ background: '#f0f3f8', borderBottom: '2px solid #d8dde8' }}>
                                    {[['Dentist', null], ['Contract', null], ['Status', 'center'], ['CDP Report', 'right'], ['Sage Export', 'right'], ['Sent', 'right'], ['Max Delta', 'right']].map(([label, align]) => (
                                        <th key={label} style={{ padding: '9px 10px', fontWeight: 700, fontSize: '0.84em', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#f0f3f8', borderBottom: '2px solid #d8dde8', textAlign: align || 'left' }}>{label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleResults.map((result, index) => {
                                    const delta = maxDelta(result.values);
                                    const isEven = index % 2 === 0;
                                    const rowBg = !loading && !result.error && result.match
                                        ? (isEven ? '#f0fff4' : '#e6ffec')
                                        : !loading && !result.match
                                            ? (isEven ? '#fff5f5' : '#ffeeee')
                                            : (isEven ? '#ffffff' : '#f8fafc');
                                    const td = { padding: '4px 8px', borderBottom: '1px solid #edf0f5', color: '#2d3748', verticalAlign: 'middle', whiteSpace: 'nowrap', lineHeight: 1.4, fontSize: '0.93em' };
                                    return (
                                        <tr key={result.doctorId} style={{ background: rowBg, transition: 'background 0.1s' }}>
                                            <td style={{ ...td, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                                                {result.doctorName || result.doctorId}
                                            </td>
                                            <td style={td}><ContractLabel contractType={result.contractType} /></td>
                                            <td style={{ ...td, textAlign: 'center' }}>
                                                {result.error
                                                    ? <Icon name='warning sign' color='orange' title={result.error} />
                                                    : result.match
                                                        ? <Icon name='check circle' color='green' />
                                                        : <Icon name='times circle' color='red' />}
                                            </td>
                                            <td style={{ ...td, textAlign: 'right' }}>{renderValueCell(result.values?.cdpReport)}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>{renderValueCell(result.values?.sageExport)}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>
                                                {result.earningsReportSent
                                                    ? renderValueCell(result.values?.earningsReport)
                                                    : <span style={{ color: '#aaa' }} title='No Earnings Report sent for this dentist/month'>Not sent</span>}
                                            </td>
                                            <td style={{ ...td, textAlign: 'right' }}>
                                                {result.error
                                                    ? '—'
                                                    : delta > 0
                                                        ? <strong style={{ color: '#b00' }}>{formatCurrency(delta)}</strong>
                                                        : <span style={{ color: '#297a29' }}>$0.00</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Content>

            {/* ── Footer: request-activity log ── */}
            {log.length > 0 && (
                <Modal.Actions style={{ textAlign: 'left', padding: '8px 16px' }}>
                    <Accordion>
                        <Accordion.Title active={logOpen} onClick={() => setLogOpen(o => !o)} style={{ padding: '6px 0' }}>
                            <Icon name='dropdown' />
                            Request activity&nbsp;
                            <span style={{ color: '#888', fontWeight: 'normal', fontSize: '0.9em' }}>
                                ({log.filter(e => e.status !== 'pending').length} responses)
                            </span>
                        </Accordion.Title>
                        <Accordion.Content active={logOpen}>
                            <Table size='small' compact basic='very' style={{ marginTop: 4 }}>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Time</Table.HeaderCell>
                                        <Table.HeaderCell>Dentist</Table.HeaderCell>
                                        <Table.HeaderCell>Surface</Table.HeaderCell>
                                        <Table.HeaderCell>Result</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {log.map((entry, idx) => {
                                        const icon = LOG_STATUS_ICON[entry.status] || LOG_STATUS_ICON.error;
                                        return (
                                            <Table.Row key={idx}>
                                                <Table.Cell style={{ whiteSpace: 'nowrap', color: '#888', fontSize: '0.85em' }}>
                                                    {moment(entry.ts).format('HH:mm:ss.SSS')}
                                                </Table.Cell>
                                                <Table.Cell>{entry.doctorName || entry.abbr}</Table.Cell>
                                                <Table.Cell>{SURFACE_LABELS[entry.surface] || entry.surface}</Table.Cell>
                                                <Table.Cell>
                                                    <Icon name={icon.name} color={icon.color} loading={!!icon.loading} />
                                                    {entry.status === 'ok'       && entry.value != null && formatCurrency(entry.value)}
                                                    {entry.status === 'not_sent' && 'Not sent'}
                                                    {entry.status === 'pending'  && 'Fetching…'}
                                                    {entry.status === 'error'    && (entry.error || 'Error')}
                                                </Table.Cell>
                                            </Table.Row>
                                        );
                                    })}
                                </Table.Body>
                            </Table>
                        </Accordion.Content>
                    </Accordion>
                </Modal.Actions>
            )}
        </Modal>
    );
};

export default TotalPayConsistencyModal;
